import { useEffect, useMemo, useState } from "react"

import { AlertTriangle, Check, Lock, ShieldCheck, UserX } from "lucide-react"
import { useTranslation } from "react-i18next"

import { useAppContext } from "@/app/providers/app-provider"

import { useCompleteCaptchaMutation } from "@/shared/api/security/security.api"

import { Button } from "@/shared/ui/button"
import { Card } from "@/shared/ui/card"

import { WebApp } from "@/shared/telegram"

import { PuzzleCaptcha } from "./puzzle-captcha"

// Forbidden Access Component
const ForbiddenPage: React.FC = () => {
  const { t } = useTranslation()

  const handleClose = () => {
    WebApp.close()
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-900 p-4">
      <div className="w-full max-w-md">
        <Card className="border-red-700 bg-gray-900 p-6 text-center shadow-lg">
          <div className="space-y-6">
            {/* Icon */}
            <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-red-500/20">
              <UserX className="h-10 w-10 text-red-500" />
            </div>

            {/* Title */}
            <div>
              <h2 className="mb-2 text-xl font-bold text-red-400">
                {t("captcha-forbidden-access-denied", "Access Denied")}
              </h2>
              <p className="mb-4 text-sm text-gray-300">
                {t(
                  "captcha-forbidden-different-user",
                  "This verification is for a different user. Please use your own account to access this page."
                )}
              </p>
            </div>

            {/* Warning Info */}
            <div className="rounded-lg border border-red-700/50 bg-red-900/30 p-4">
              <div className="flex items-start space-x-3">
                <Lock className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500" />
                <div className="text-left">
                  <p className="mb-1 text-sm font-medium text-red-400">
                    {t("captcha-forbidden-security-notice", "Security Notice")}
                  </p>
                  <p className="text-xs text-gray-400">
                    {t(
                      "captcha-forbidden-security-description",
                      "For security reasons, captcha verification must be completed by the intended user. If you believe this is an error, please contact the group administrator."
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Close Button */}
            <Button
              onClick={handleClose}
              className="w-full bg-red-600 text-white hover:bg-red-700">
              {t("captcha-forbidden-close", "Close")}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  )
}

export const CaptchaPage: React.FC = () => {
  const { t } = useTranslation()
  const [requiresNewRequest, setRequiresNewRequest] = useState(false)
  const [isVerified, setIsVerified] = useState(false)
  const [verificationError, setVerificationError] = useState<string | null>(null)
  const [captchaAttempt, setCaptchaAttempt] = useState(0)
  const [closeCountdown, setCloseCountdown] = useState<number | null>(null)
  const { userId, chatId, joinRequestAttemptId } = useAppContext()
  const [completeCaptcha] = useCompleteCaptchaMutation()

  // Get current Telegram user data
  const currentUserId = WebApp.initDataUnsafe.user?.id

  // Check if current user matches the context user
  const isUserMismatch = useMemo(() => {
    // If no userId in context, allow access
    if (!userId) return false

    // If no current user from Telegram, allow access (might be in development)
    if (!currentUserId) return false

    return userId !== currentUserId
  }, [userId, currentUserId])

  const resetAfterFailedCompletion = () => {
    WebApp.HapticFeedback.notificationOccurred("error")
    setVerificationError(t("captcha-page-completion-failed-description"))
    setCaptchaAttempt(attempt => attempt + 1)
  }

  const completePuzzle = async () => {
    setVerificationError(null)

    if (chatId === null || userId === null) {
      resetAfterFailedCompletion()
      return
    }

    try {
      const response = await completeCaptcha({
        chat_id: chatId,
        user_id: userId,
        join_request_attempt_id: joinRequestAttemptId ?? undefined
      }).unwrap()

      if (response.result.retry_join_request) {
        WebApp.HapticFeedback.notificationOccurred("warning")
        setRequiresNewRequest(true)
        setVerificationError(t("captcha-page-rejoin-description"))
        return
      }

      if (!response.result.success) {
        resetAfterFailedCompletion()
        return
      }

      WebApp.HapticFeedback.notificationOccurred("success")
      setIsVerified(true)
      setCloseCountdown(5)
    } catch (error) {
      console.error("Failed to complete puzzle captcha:", error)
      resetAfterFailedCompletion()
    }
  }

  const handlePuzzleSolved = () => {
    void completePuzzle()
  }

  // Auto-close countdown timer after success
  useEffect(() => {
    if (closeCountdown !== null && closeCountdown > 0) {
      const timer = setTimeout(() => {
        setCloseCountdown(prev => (prev ?? 0) - 1)
      }, 1000)
      return () => {
        clearTimeout(timer)
      }
    } else if (closeCountdown === 0) {
      WebApp.close()
    }
  }, [closeCountdown])

  // Show forbidden page if user mismatch
  if (isUserMismatch) {
    return <ForbiddenPage />
  }

  return (
    <main
      className="flex min-h-dvh items-center justify-center bg-gray-900 px-4 py-6"
      data-testid="captcha-page">
      <section className="w-full max-w-sm rounded-2xl bg-gray-800 p-4 sm:p-5">
        {isVerified ? (
          <div className="py-4 text-center">
            <div className="mx-auto flex size-14 items-center justify-center rounded-full bg-green-500/15">
              <Check
                aria-hidden="true"
                focusable="false"
                className="size-7 text-green-400"
              />
            </div>
            <h1 className="mt-4 text-lg font-semibold text-gray-100">
              {t("captcha-page-welcome-group", "Welcome to the Group!")}
            </h1>
            <p className="mt-1 text-sm text-gray-400">
              {t(
                "captcha-page-verified-access",
                "You have been successfully verified and can now participate in the chat."
              )}
            </p>
            {closeCountdown !== null && closeCountdown > 0 && (
              <p className="mt-4 text-xs tabular-nums text-gray-400">
                {t("captcha-page-closing-countdown", "Closing in {{countdown}}...", { countdown: closeCountdown })}
              </p>
            )}
          </div>
        ) : (
          <>
            <div className="mb-4 flex items-center gap-3">
              <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-yellow-500/10">
                <ShieldCheck
                  aria-hidden="true"
                  focusable="false"
                  className="size-5 text-yellow-500"
                />
              </div>
              <h1 className="text-base font-semibold text-gray-100">
                {t("captcha-page-verification", "Confirm you're human")}
              </h1>
            </div>
            {verificationError && (
              <div
                className="mb-4 flex items-start gap-2.5 rounded-xl bg-red-500/[0.08] p-3 shadow-[inset_2px_0_0_0] shadow-red-400/70"
                role="alert">
                <AlertTriangle
                  aria-hidden="true"
                  focusable="false"
                  className="mt-0.5 size-4 shrink-0 text-red-400"
                />
                <div>
                  <p className="text-sm font-medium text-red-300">
                    {requiresNewRequest
                      ? t("captcha-page-rejoin-title")
                      : t("captcha-page-completion-failed-title", "Verification failed")}
                  </p>
                  <p className="mt-0.5 text-xs text-gray-300">{verificationError}</p>
                </div>
              </div>
            )}
            {requiresNewRequest ? (
              <Button
                onClick={() => {
                  WebApp.close()
                }}
                className="w-full">
                {t("captcha-forbidden-close", "Close")}
              </Button>
            ) : (
              <PuzzleCaptcha
                key={captchaAttempt}
                onSolved={handlePuzzleSolved}
              />
            )}
          </>
        )}
      </section>
    </main>
  )
}
